import { useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { Logger } from '@egw/logger'
import { useCustomerDetails, useEditLinkedAchAccountName } from '@egw/api'
import { Button, ButtonVariant, Input } from '@egw/components'
import { CUSTOMER_SERVICE_PHONE_NUMBER } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/24/solid'

const log = new Logger('UpdatePasskeyForm')

export interface UpdateAccountFormProps {
  alias: number
  onSuccess: () => void
}

interface FormData {
  name: string
}

export const UpdateAccountForm: FC<UpdateAccountFormProps> = ({ alias, onSuccess }) => {
  const [isUpdatingAccountNameLoading, setIsUpdatingAccountNameLoading] = useState(false)
  const [formError, setFormError] = useState(false)

  const { t } = useTranslation('features-external-accounts')

  const { data, isLoading, isError, refetch } = useCustomerDetails()
  const account = data?.customer.linkedAccounts.achAccounts.find((a) => a.alias === alias)

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isDirty, isValid },
  } = useForm({
    defaultValues: {
      name: account?.name ?? '',
    },
  })

  const { mutate: updateAccountName } = useEditLinkedAchAccountName({
    onError: () => {
      setFormError(true)
    },
  })

  const onSubmit = handleSubmit(async (formData: unknown): Promise<void> => {
    setFormError(false)
    setIsUpdatingAccountNameLoading(true)
    const d = formData as FormData
    try {
      await updateAccountName({ alias, accountName: d.name })
      await refetch()
      onSuccess()
    } catch (error) {
      log.error(error)
      setFormError(true)
    } finally {
      setIsUpdatingAccountNameLoading(false)
    }
  })

  return (
    <>
      {isLoading ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={5} height="80px" />
        </SkeletonTheme>
      ) : null}

      {isError ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('details.error_title')}</p>
          <p className="text-body text-xl">{t('details.error_message')}</p>
        </div>
      ) : null}

      {formError ? (
        <div className="flex bg-alertErrorBg p-3 mb-4 rounded-md">
          <InformationCircleIcon className="w-6 h-6 fill-red-500" />
          <p className="text-sm text-alertErrorText ml-2">
            {t('details.update_error')}{' '}
            <a
              className="text-btnLinkText hover:text-btnLinkTextHover hover:underline font-semibold"
              href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
            >
              {t('details.contact')}
            </a>
          </p>
        </div>
      ) : null}

      {account ? (
        <form>
          <div className="mb-4">
            <Controller
              name="name"
              control={control}
              rules={{
                required: true,
                // matches Plaid Account Name length
                maxLength: 30,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('details.update_name_label')}
                  id="externalAccountNickname"
                  testId="externalAccountNickname_input"
                  required
                  error={formErrors.name?.message}
                  // matches Plaid Account Name length
                  maxLength={30}
                />
              )}
            />
          </div>

          <Button
            variant={ButtonVariant.Primary}
            className="w-full"
            onClick={(e) => void onSubmit(e)}
            isDisabled={!isDirty || !isValid}
            isLoading={isUpdatingAccountNameLoading}
            testId="externalAccountNicknameChangeContinue_primary_btn"
          >
            {t('details.save')}
          </Button>
        </form>
      ) : null}
    </>
  )
}
